import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  getCertificate, issueCertificate, certPdfUrl, certCsvUrl, certJsonUrl,
  fileUrl, getBoardLayout,
} from '../../services/api';
import { ensureAccessToken } from '../../services/session';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ShieldCheck, FileText, Code2, Sheet, Loader2,
  LayoutPanelTop, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const download = (url, filename) => {
  return ensureAccessToken()
    .then(token => fetch(url, { headers: { Authorization: `Bearer ${token}` } }))
    .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
    .then(b => {
      const u = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
    });
};

/**
 * Mirror of backend `_collapse_three_pole_gangs` — merges adjacent
 * `NL1` / `NL2` / `NL3` rows into one `NTP` row when an explicit
 * `isThreePole` flag is set OR when description + rating match across
 * the triplet. Keeps the on-screen cert table aligned with what the
 * PDF will render.
 */
const collapseThreePoleGangs = (circuits) => {
  if (!Array.isArray(circuits) || !circuits.length) return circuits || [];
  const norm = (s) => String(s || '').trim().toUpperCase();
  const groups = {};
  circuits.forEach((c, idx) => {
    if (!c || typeof c !== 'object') return;
    const num = String(c.number || c.circuitNumber || '').trim();
    const m = num.match(/^(\d+)\s*L([123])$/i);
    if (!m) return;
    const way = m[1];
    const phase = m[2];
    if (!groups[way]) groups[way] = {};
    groups[way][phase] = idx;
  });
  const drop = new Set();
  const rewrite = {};
  Object.entries(groups).forEach(([way, ph]) => {
    if (!ph['1'] && ph['1'] !== 0) return;
    if (!ph['2'] && ph['2'] !== 0) return;
    if (!ph['3'] && ph['3'] !== 0) return;
    const l1 = circuits[ph['1']], l2 = circuits[ph['2']], l3 = circuits[ph['3']];
    const explicit = !!(l1.isThreePole || l2.isThreePole || l3.isThreePole);
    const descMatch = norm(l1.description) === norm(l2.description) &&
                      norm(l2.description) === norm(l3.description) && norm(l1.description);
    const ratingMatch = norm(l1.mcbRating) === norm(l2.mcbRating) &&
                        norm(l2.mcbRating) === norm(l3.mcbRating) && norm(l1.mcbRating);
    if (!(explicit || (descMatch && ratingMatch))) return;
    const merged = { ...l1, number: `${way}TP`, isThreePole: true };
    ['description', 'cableSize', 'mcbType', 'mcbRating', 'rcdRating',
     'insulationResistance', 'earthLoopImpedance', 'rcdTripTime',
     'polarity', 'notes'].forEach(fld => {
      if (!String(merged[fld] || '').trim()) {
        for (const sib of [l2, l3]) {
          if (String(sib[fld] || '').trim()) { merged[fld] = sib[fld]; break; }
        }
      }
    });
    rewrite[ph['1']] = merged;
    drop.add(ph['2']); drop.add(ph['3']);
  });
  return circuits
    .map((c, i) => (rewrite[i] ? rewrite[i] : c))
    .filter((_, i) => !drop.has(i));
};

const CertView = () => {
  const { id } = useParams();
  const location = useLocation();
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState(false);
  // Layout the cert was authored against. We fetch it lazily so certs
  // without a layout don't pay the round-trip.
  const [layout, setLayout] = useState(null);
  const refresh = () => getCertificate(id).then(setCert);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!cert?.layoutId) { setLayout(null); return; }
    getBoardLayout(cert.layoutId)
      .then(setLayout)
      .catch(() => setLayout(null));
  }, [cert?.layoutId]);

  if (!cert) return <div className="p-8 text-slate-500">Loading...</div>;

  const issue = async () => {
    if (!window.confirm('Confirm: I have verified all values and am signing this certificate as the qualified inspecting engineer.')) return;
    setBusy(true);
    try {
      await issueCertificate(id);
      await refresh();
      toast.success('Certificate issued');
    } catch (e) {
      toast.error('Issue failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="cert-view">
      <Link to={`${base}/certificates`} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to certificates</Link>
      <PageHeader
        title={`${cert.certType} · ${cert.id}`}
        description={cert.issued ? `Issued ${cert.issuedAt?.slice(0,10)} by ${cert.issuedBy}` : 'Draft — awaiting engineer sign-off'}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => download(certPdfUrl(id), `lutrom-${id}.pdf`)}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
            <Button variant="outline" onClick={() => download(certCsvUrl(id), `lutrom-${id}.csv`)}><Sheet className="h-4 w-4 mr-1" /> CSV</Button>
            <Button variant="outline" onClick={() => download(certJsonUrl(id), `lutrom-${id}.json`)}><Code2 className="h-4 w-4 mr-1" /> JSON</Button>
            {!cert.issued && (
              <Button onClick={issue} disabled={busy} className="bg-blue-700 hover:bg-blue-800 text-white font-semibold">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Sign &amp; issue
              </Button>
            )}
          </div>
        }
      />

      {/* Layout coverage gap-check — only renders when the cert was
          created with a board layout AND has at least one photo. */}
      {layout && cert.photoUrls?.length > 0 && (
        <LayoutCoverage layout={layout} circuits={cert.circuits || []} firstPhotoUrl={cert.photoUrls[0]} />
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Field label="Site" value={cert.siteAddress} />
            <Field label="Client" value={cert.clientName} />
            <Field label="Inspector" value={cert.inspectorName} />
            <Field label="Reg No." value={cert.inspectorRegNumber} />
            <Field label="Date inspected" value={cert.dateOfInspection} />
            <Field label="Next inspection" value={cert.nextInspectionDate} />
            <Field label="Supply type" value={cert.supplyType} />
            <Field label="Voltage / Freq" value={`${cert.nominalVoltage || '—'} · ${cert.nominalFrequency || '—'}`} />
            <Field label="Earth-fault loop Ze" value={cert.earthFaultLoopImpedance} />
            <Field label="Prospective fault Ipf" value={cert.prospectiveFaultCurrent} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-blue-700 mb-1">Overall condition</div>
            <div className={`text-base font-semibold ${cert.overallCondition?.toLowerCase().startsWith('satisf') ? 'text-green-700' : 'text-red-700'}`}>{cert.overallCondition || '—'}</div>
          </div>
          {cert.observations && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-blue-700 mb-1">Observations</div>
              <p className="text-slate-800 text-sm whitespace-pre-line">{cert.observations}</p>
            </div>
          )}
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-blue-700 mb-2">Circuits</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[700px]">
                <thead className="bg-slate-50 border-y border-slate-200 text-left">
                  <tr>
                    {['#', 'Description', 'Cable', 'MCB', 'RCD', 'IR', 'Zs', 'Trip', 'Pol.'].map(h => <th key={h} className="px-2 py-2 font-medium text-slate-600">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {collapseThreePoleGangs(cert.circuits || []).map((c, i) => (
                    <tr key={i}>
                      <td className="px-2 py-2 font-mono">
                        {c.number}
                        {c.isThreePole && /TP$/i.test(String(c.number || '')) && (
                          <span className="ml-1 text-[9px] uppercase tracking-wide font-semibold text-indigo-700 bg-indigo-50 px-1 py-0.5 rounded" title="3-pole gang — L1 + L2 + L3 collapsed">3φ</span>
                        )}
                      </td>
                      <td className="px-2 py-2">{c.description}</td>
                      <td className="px-2 py-2">{c.cableSize}</td>
                      <td className="px-2 py-2">{c.mcbType}{c.mcbRating}</td>
                      <td className="px-2 py-2">{c.rcdRating}</td>
                      <td className="px-2 py-2">{c.insulationResistance}</td>
                      <td className="px-2 py-2">{c.earthLoopImpedance}</td>
                      <td className="px-2 py-2">{c.rcdTripTime}</td>
                      <td className="px-2 py-2">{c.polarity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <aside className="bg-white border border-slate-200 rounded-md p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-blue-700 mb-3">Photos used</div>
          {cert.photoUrls?.length ? (
            <div className="grid grid-cols-2 gap-2">
              {cert.photoUrls.map((u, i) => (
                <a key={i} href={fileUrl(u)} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={fileUrl(u)} alt={`photo-${i}`} className="h-24 w-full object-cover rounded border border-slate-200 hover:border-blue-700" />
                </a>
              ))}
            </div>
          ) : <div className="text-sm text-slate-500">No photos attached.</div>}
          <div className="mt-5 pt-5 border-t border-slate-200 text-xs text-slate-500">
            <div><span className="font-semibold">AI assisted:</span> {cert.aiAssisted ? 'Yes (Claude Sonnet 4.5 vision)' : 'No'}</div>
            {cert.layoutId && <div><span className="font-semibold">Layout:</span> {layout?.name || cert.layoutId}</div>}
            <div><span className="font-semibold">Created by:</span> {cert.createdBy}</div>
            <div><span className="font-semibold">Created at:</span> {cert.createdAt?.slice(0, 16).replace('T', ' ')}</div>
          </div>
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
            <div className="font-semibold text-slate-900 mb-1">Tysoft EasyCert export</div>
            Use the <span className="font-medium">JSON</span> or <span className="font-medium">CSV</span> buttons above to export structured circuit data. We'll match Tysoft's exact import schema once you share a sample file.
          </div>
        </aside>
      </div>
    </div>
  );
};

// ─────────────────────── Layout coverage panel ───────────────────────
/**
 * Returns true when the circuit linked to a frame has been "filled" —
 * meaning the engineer has at least one substantive value populated.
 * Isolator frames are auto-filled (they don't map to a circuit).
 */
const isFrameFilled = (frame, circuits) => {
  if (frame.type === 'isolator') return true;
  const linked = circuits.find((c) => c.frameId && c.frameId === frame.id);
  if (!linked) return false;
  if (frame.type === 'circuit_label') {
    return Boolean((linked.description || '').trim());
  }
  // breaker — needs at least one of cableSize / mcbRating / rcdRating
  return Boolean(
    (linked.cableSize || '').trim() ||
    (linked.mcbRating || '').trim() ||
    (linked.rcdRating || '').trim()
  );
};

const FRAME_FILLED_STYLES = {
  breaker: { bg: 'rgba(16,185,129,0.35)', border: 'rgb(16,185,129)' },
  isolator: { bg: 'rgba(16,185,129,0.35)', border: 'rgb(16,185,129)' },
  circuit_label: { bg: 'rgba(16,185,129,0.35)', border: 'rgb(16,185,129)' },
};
const FRAME_UNFILLED_STYLES = {
  breaker: { bg: 'rgba(239,68,68,0.45)', border: 'rgb(239,68,68)' },
  isolator: { bg: 'rgba(239,68,68,0.45)', border: 'rgb(239,68,68)' },
  circuit_label: { bg: 'rgba(239,68,68,0.45)', border: 'rgb(239,68,68)' },
};

const LayoutCoverage = ({ layout, circuits, firstPhotoUrl }) => {
  const frames = layout.frames || [];
  const filled = frames.filter((f) => isFrameFilled(f, circuits));
  const unfilled = frames.filter((f) => !isFrameFilled(f, circuits));
  const allOk = unfilled.length === 0;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="cert-layout-coverage">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-blue-700 mb-1">
            <LayoutPanelTop className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Layout coverage — {layout.name}
          </div>
          <p className="text-xs text-slate-500">
            Frames the AI mapped to a circuit are <span className="text-emerald-700 font-medium">green</span>.
            Frames with no matching or empty circuit are <span className="text-red-700 font-medium">red</span> — re-check before sign-off.
          </p>
        </div>
        <div
          className={`text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
            allOk
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
          data-testid="cert-layout-coverage-badge"
        >
          {allOk
            ? <><CheckCircle2 className="h-4 w-4" /> All {frames.length} frames filled</>
            : <><AlertTriangle className="h-4 w-4" /> {filled.length} / {frames.length} frames filled</>}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 relative bg-slate-100 border border-slate-300 rounded overflow-hidden" style={{ aspectRatio: '4 / 3' }}>
          <img
            src={fileUrl(firstPhotoUrl)}
            alt="Source"
            className="absolute inset-0 w-full h-full object-contain"
            draggable={false}
          />
          {frames.map((f) => {
            const ok = isFrameFilled(f, circuits);
            const style = (ok ? FRAME_FILLED_STYLES : FRAME_UNFILLED_STYLES)[f.type] || FRAME_UNFILLED_STYLES.breaker;
            return (
              <div
                key={f.id}
                className="absolute pointer-events-none"
                style={{
                  left: `${f.x}%`,
                  top: `${f.y}%`,
                  width: `${f.w}%`,
                  height: `${f.h}%`,
                  backgroundColor: style.bg,
                  border: `2px solid ${style.border}`,
                  boxShadow: ok ? 'none' : '0 0 0 3px rgba(239,68,68,0.25)',
                }}
                title={`${f.type} ${f.label || ''}${ok ? ' — filled' : ' — MISSING'}`}
              />
            );
          })}
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {unfilled.length > 0 && (
            <>
              <div className="text-xs font-medium uppercase tracking-wider text-red-700">Missing</div>
              {unfilled.map((f) => (
                <div key={f.id} className="flex items-center justify-between text-xs bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  <span className="font-medium text-red-700">
                    {f.label || '(no label)'}
                  </span>
                  <span className="text-[10px] text-slate-500 uppercase">{f.type.replace('_', ' ')}</span>
                </div>
              ))}
            </>
          )}
          {filled.length > 0 && (
            <>
              <div className="text-xs font-medium uppercase tracking-wider text-emerald-700 mt-3">Filled</div>
              {filled.map((f) => (
                <div key={f.id} className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  <span className="font-medium text-emerald-800">
                    {f.label || '(no label)'}
                  </span>
                  <span className="text-[10px] text-slate-500 uppercase">{f.type.replace('_', ' ')}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm font-medium text-slate-900">{value || '—'}</div>
  </div>
);

export default CertView;
